import {
  Switch,
  Flex,
  Modal,
  Button,
  Input,
  Tooltip,
} from "antd";
import { useEditProductContext } from "../../../contexts/edit-product-context/useEditProductContext";
import { useBranchContext } from "../../../contexts/branch-context/useBranchContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Clock, Info, X, PlusCircle } from "@phosphor-icons/react";

import styled from "styled-components";
import { colors } from "../../../styles/theme";

const ActionButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.primary};
  color: ${colors.primary};
  background: var(--color-surface);
  margin-top: 8px;

  &:hover {
    background: ${colors.primaryLight} !important;
    border-color: ${colors.primary} !important;
    color: ${colors.primary} !important;
  }

  svg {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

// === Nowy design identyczny z OnlineSalesSettings ===

const DayAvatar = styled.div<{ $enabled: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $enabled }) => ($enabled ? colors.primary : "#e8edf0")};
  color: ${({ $enabled }) => ($enabled ? "#fff" : colors.textSecondary)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`;

const DayRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const DayLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const UnavailableLabel = styled.span`
  color: ${colors.textSecondary};
  font-size: 14px;
  flex: 1;
`;

const TimeRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const TimeInput = styled(Input)`
  flex: 1;
  min-width: 60px;
`;

const TimeSeparator = styled.span`
  color: ${colors.textSecondary};
  font-size: 16px;
  flex-shrink: 0;
`;

const IconButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  flex-shrink: 0;
`;

const AddRangeIconButton = styled(IconButton)`
  color: ${colors.primary};
  &:hover {
    color: ${colors.primary} !important;
    background: ${colors.primaryLight} !important;
  }
`;

const RemoveRangeIconButton = styled(IconButton)`
  color: ${colors.textSecondary};
  &:hover {
    color: ${colors.error} !important;
    background: transparent !important;
  }
`;

const IconSpacer = styled.div`
  flex-shrink: 0;
`;

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const dayAbbreviationKeys: Record<string, string> = {
  monday: "productVisibilityData.dayAbbrMonday",
  tuesday: "productVisibilityData.dayAbbrTuesday",
  wednesday: "productVisibilityData.dayAbbrWednesday",
  thursday: "productVisibilityData.dayAbbrThursday",
  friday: "productVisibilityData.dayAbbrFriday",
  saturday: "productVisibilityData.dayAbbrSaturday",
  sunday: "productVisibilityData.dayAbbrSunday",
};

const InfoIcon = styled(Info)`
  cursor: pointer;
  color: ${colors.textSecondary};
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary};
  }
`;

const BranchSection = styled.div`
  margin-bottom: 24px;
`;

const BranchTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const ShowMoreButton = styled(Button)`
  color: ${colors.primary};
  font-size: 14px;
  margin-top: 8px;

  &:hover {
    color: ${colors.primary} !important;
  }
`;

export const ProductVisibilityData: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch, availableBranches: contextAvailableBranches } = useBranchContext();

  const {
    branchVisibility,
    setBranchVisibility,
    setUnsavedChanges,
    availableBranches,
    setAvailableBranches,
    onlyInternalSell,
    setOnlyInternalSell,
  } = useEditProductContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [showAllBranches, setShowAllBranches] = useState(false);
  const [currentScheduleBranchId, setCurrentScheduleBranchId] = useState<number | null>(null);

  const showModal = (branchId: number) => {
    setCurrentScheduleBranchId(branchId);
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    setCurrentScheduleBranchId(null);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentScheduleBranchId(null);
  };

  const addRange = (day: string) => {
    if (currentScheduleBranchId === null) return;
    setBranchVisibility((current: any) => ({
      ...current,
      [currentScheduleBranchId]: {
        ...current[currentScheduleBranchId],
        availabilitySchedules: {
          ...current[currentScheduleBranchId].availabilitySchedules,
          [day]: [...(current[currentScheduleBranchId].availabilitySchedules[day] || []), { from: "8:00", till: "16:00" }],
        },
      },
    }));
    setUnsavedChanges(true);
  };

  const removeSpecificRange = (day: string, index: number) => {
    if (currentScheduleBranchId === null) return;
    setBranchVisibility((current: any) => {
      const newArray = [...(current[currentScheduleBranchId].availabilitySchedules[day] || [])];
      newArray.splice(index, 1);
      return {
        ...current,
        [currentScheduleBranchId]: {
          ...current[currentScheduleBranchId],
          availabilitySchedules: {
            ...current[currentScheduleBranchId].availabilitySchedules,
            [day]: newArray,
          },
        },
      };
    });
    setUnsavedChanges(true);
  };

  const isDayEnabled = (day: string) => {
    if (currentScheduleBranchId === null) return false;
    return branchVisibility[currentScheduleBranchId]?.availabilitySchedules?.[day]?.length > 0;
  };

  const toggleDay = (day: string, enabled: boolean) => {
    if (enabled) {
      addRange(day);
    } else {
      if (currentScheduleBranchId === null) return;
      setBranchVisibility((current: any) => ({
        ...current,
        [currentScheduleBranchId]: {
          ...current[currentScheduleBranchId],
          availabilitySchedules: {
            ...current[currentScheduleBranchId].availabilitySchedules,
            [day]: [],
          },
        },
      }));
      setUnsavedChanges(true);
    }
  };

  const updateTimeRange = (day: string, index: number, field: 'from' | 'till', time: string) => {
    if (currentScheduleBranchId === null) return;
    setBranchVisibility((current: any) => {
      const newArray = [...(current[currentScheduleBranchId].availabilitySchedules[day] || [])];
      newArray[index] = {
        ...newArray[index],
        [field]: time,
      };
      return {
        ...current,
        [currentScheduleBranchId]: {
          ...current[currentScheduleBranchId],
          availabilitySchedules: {
            ...current[currentScheduleBranchId].availabilitySchedules,
            [day]: newArray,
          },
        },
      };
    });
    setUnsavedChanges(true);
  };

  const handleTimeInput = (value: string, day: string, index: number, field: 'from' | 'till') => {
    // Usuń wszystko oprócz cyfr
    let cleaned = value.replace(/[^\d]/g, '');

    // Ogranicz do 4 cyfr (HHMM)
    if (cleaned.length > 4) {
      cleaned = cleaned.slice(0, 4);
    }

    // Formatuj jako HH:MM
    let formatted = cleaned;
    if (cleaned.length >= 3) {
      formatted = cleaned.slice(0, 2) + ':' + cleaned.slice(2);
    }

    // Waliduj godziny i minuty
    if (cleaned.length >= 2) {
      const hours = parseInt(cleaned.slice(0, 2));
      if (hours > 23) {
        formatted = '23' + (cleaned.length > 2 ? ':' + cleaned.slice(2) : '');
      }
    }

    if (cleaned.length === 4) {
      const minutes = parseInt(cleaned.slice(2, 4));
      if (minutes > 59) {
        formatted = cleaned.slice(0, 2) + ':59';
      }
    }

    updateTimeRange(day, index, field, formatted);
  };

  const normalizeTime = (value: string, day: string, index: number, field: 'from' | 'till') => {
    const digits = value.replace(/[^\d]/g, '');
    if (!digits) return;
    let hours: number;
    let minutes = 0;
    if (digits.length <= 2) {
      hours = parseInt(digits, 10);
    } else {
      hours = parseInt(digits.slice(0, 2), 10);
      minutes = parseInt(digits.slice(2, 4).padEnd(2, '0'), 10);
    }
    if (hours > 23) hours = 23;
    if (minutes > 59) minutes = 59;
    updateTimeRange(day, index, field, `${hours}:${String(minutes).padStart(2, '0')}`);
  };

  const toggleBranchVisibility = (branchId: number, field: 'isVisible' | 'isAvailable', value: boolean) => {
    const defaultSchedules = {
      friday: [{ from: "00:00", till: "23:45" }],
      monday: [{ from: "00:00", till: "23:45" }],
      sunday: [{ from: "00:00", till: "23:45" }],
      tuesday: [{ from: "00:00", till: "23:45" }],
      saturday: [{ from: "00:00", till: "23:45" }],
      thursday: [{ from: "00:00", till: "23:45" }],
      wednesday: [{ from: "00:00", till: "23:45" }],
    };

    setBranchVisibility((current: any) => {
      // Ensure branch data exists with defaults
      const currentBranchData = current[branchId] || {
        isVisible: true,
        isAvailable: true,
        availabilitySchedules: defaultSchedules,
      };

      const newBranchData = {
        ...currentBranchData,
        [field]: value,
      };

      // If hiding from menu (isVisible = false), also set isAvailable to false
      if (field === 'isVisible' && !value) {
        newBranchData.isAvailable = false;
      }

      // If showing in menu (isVisible = true), also set isAvailable to true
      if (field === 'isVisible' && value) {
        newBranchData.isAvailable = true;
      }

      return {
        ...current,
        [branchId]: newBranchData,
      };
    });

    // If enabling visibility for a branch not in availableBranches, add it
    if (field === 'isVisible' && value) {
      const branchExists = availableBranches.some((b: any) => b.id === branchId);
      if (!branchExists) {
        const branchToAdd = contextAvailableBranches.find((b: any) => b.id === branchId);
        if (branchToAdd) {
          setAvailableBranches((current: any) => [...current, branchToAdd]);
        }
      }
    }

    setUnsavedChanges(true);
  };

  const hasMultipleBranches = contextAvailableBranches.length > 1;

  // Get other branches (not the current one)
  const otherBranches = contextAvailableBranches.filter(
    (branch) => branch.id !== currentBranch?.id
  );

  return (
    <>
      {/* Only POS / Internal Sell */}
      <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
        <Switch
          checked={onlyInternalSell}
          onChange={(value) => {
            setOnlyInternalSell(value);
            setUnsavedChanges(true);
          }}
        />
        <span>{t("productVisibilityData.onlyInternalSell")}</span>
        <Tooltip title={t("productVisibilityData.onlyInternalSellTooltip")}>
          <InfoIcon size={18} weight="fill" />
        </Tooltip>
      </Flex>

      {/* Current Branch Section */}
      {currentBranch && (
        <BranchSection>
          <BranchTitle>
            {hasMultipleBranches ? currentBranch.name : t("productVisibilityData.productVisibility")}
          </BranchTitle>
          <Flex vertical gap={12}>
            <Flex align="center" gap={8}>
              <Switch
                checked={branchVisibility[currentBranch.id]?.isVisible ?? true}
                onChange={(value) => toggleBranchVisibility(currentBranch.id, 'isVisible', value)}
              />
              <span>{t("productVisibilityData.visibilityInMenu")}</span>
            </Flex>
            <Flex align="center" gap={8}>
              <Tooltip
                title={!(branchVisibility[currentBranch.id]?.isVisible ?? true) ? t("productVisibilityData.availabilityDisabledTooltip") : ""}
              >
                <span>
                  <Switch
                    checked={(branchVisibility[currentBranch.id]?.isAvailable ?? true) && (branchVisibility[currentBranch.id]?.isVisible ?? true)}
                    disabled={!(branchVisibility[currentBranch.id]?.isVisible ?? true)}
                    onChange={(value) => toggleBranchVisibility(currentBranch.id, 'isAvailable', value)}
                  />
                </span>
              </Tooltip>
              <span>{t("productVisibilityData.available")}</span>
              <InfoIcon
                size={18}
                weight="fill"
                onClick={() => setIsInfoModalOpen(true)}
              />
            </Flex>
            <ActionButton onClick={() => showModal(currentBranch.id)}>
              <Clock size={16} />
              {t("productVisibilityData.availabilityHours")}
            </ActionButton>
          </Flex>
        </BranchSection>
      )}

      {/* Other Branches - Show More */}
      {hasMultipleBranches && otherBranches.length > 0 && (
        <>
          {!showAllBranches ? (
            <ShowMoreButton
              type="link"
              onClick={() => setShowAllBranches(true)}
            >
              {t("productVisibilityData.showOtherBranches")}
            </ShowMoreButton>
          ) : (
            <>
              {otherBranches
                .sort((a: any, b: any) => a.id - b.id)
                .map((branch: any) => (
                  <BranchSection key={branch.id}>
                    <BranchTitle>{branch.name}</BranchTitle>
                    <Flex vertical gap={12}>
                      <Flex align="center" gap={8}>
                        <Switch
                          checked={branchVisibility[branch.id]?.isVisible ?? true}
                          onChange={(value) => toggleBranchVisibility(branch.id, 'isVisible', value)}
                        />
                        <span>{t("productVisibilityData.visibilityInMenu")}</span>
                      </Flex>
                      <Flex align="center" gap={8}>
                        <Tooltip
                          title={!(branchVisibility[branch.id]?.isVisible ?? true) ? t("productVisibilityData.availabilityDisabledTooltip") : ""}
                        >
                          <span>
                            <Switch
                              checked={(branchVisibility[branch.id]?.isAvailable ?? true) && (branchVisibility[branch.id]?.isVisible ?? true)}
                              disabled={!(branchVisibility[branch.id]?.isVisible ?? true)}
                              onChange={(value) => toggleBranchVisibility(branch.id, 'isAvailable', value)}
                            />
                          </span>
                        </Tooltip>
                        <span>{t("productVisibilityData.available")}</span>
                      </Flex>
                      <ActionButton onClick={() => showModal(branch.id)}>
                        <Clock size={16} />
                        {t("productVisibilityData.availabilityHours")}
                      </ActionButton>
                    </Flex>
                  </BranchSection>
                ))}
            </>
          )}
        </>
      )}

      <Modal
        width={500}
        title={currentScheduleBranchId !== null && contextAvailableBranches.length > 1
          ? t("productVisibilityData.productAvailabilityHoursWithBranch", { branch: contextAvailableBranches.find((b: any) => b.id === currentScheduleBranchId)?.name || '' })
          : t("productVisibilityData.productAvailabilityHours")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("productVisibilityData.save")}
        cancelText={t("productVisibilityData.cancel")}
        centered
      >
        <div style={{ marginTop: "16px" }}>
          {currentScheduleBranchId !== null && DAY_ORDER.map((day) => {
            const schedules = branchVisibility[currentScheduleBranchId]?.availabilitySchedules || {};
            const ranges: any = schedules[day] ?? [];
            const enabled = isDayEnabled(day);
            return (
              <DayRow key={day}>
                {!enabled ? (
                  <DayLine>
                    <DayAvatar $enabled={false}>{t(dayAbbreviationKeys[day])}</DayAvatar>
                    <UnavailableLabel>{t("productVisibilityData.salesUnavailable")}</UnavailableLabel>
                    <AddRangeIconButton
                      type="text"
                      icon={<PlusCircle size={22} weight="regular" />}
                      onClick={() => addRange(day)}
                    />
                    <IconSpacer />
                  </DayLine>
                ) : (
                  ranges.map((range: any, i: number) => (
                    <DayLine key={i}>
                      {i === 0
                        ? <DayAvatar $enabled={true}>{t(dayAbbreviationKeys[day])}</DayAvatar>
                        : <IconSpacer />
                      }
                      <TimeRangeRow>
                        <TimeInput
                          value={range.from || ''}
                          onChange={(e) => handleTimeInput(e.target.value, day, i, 'from')}
                          onBlur={(e) => normalizeTime(e.target.value, day, i, 'from')}
                          placeholder="HH:MM"
                          maxLength={5}
                        />
                        <TimeSeparator>–</TimeSeparator>
                        <TimeInput
                          value={range.till || ''}
                          onChange={(e) => handleTimeInput(e.target.value, day, i, 'till')}
                          onBlur={(e) => normalizeTime(e.target.value, day, i, 'till')}
                          placeholder="HH:MM"
                          maxLength={5}
                        />
                      </TimeRangeRow>
                      {i === 0
                        ? <AddRangeIconButton
                          type="text"
                          icon={<PlusCircle size={22} weight="regular" />}
                          onClick={() => addRange(day)}
                        />
                        : <IconSpacer />
                      }
                      <RemoveRangeIconButton
                        type="text"
                        icon={<X size={18} weight="regular" />}
                        onClick={() => removeSpecificRange(day, i)}
                      />
                    </DayLine>
                  ))
                )}
              </DayRow>
            );
          })}
        </div>
      </Modal>

      <Modal
        title={t("productVisibilityData.availabilityInfoTitle")}
        open={isInfoModalOpen}
        onOk={() => setIsInfoModalOpen(false)}
        onCancel={() => setIsInfoModalOpen(false)}
        okText={t("productVisibilityData.understood")}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>
          {t("productVisibilityData.availabilityInfoBefore")}<strong>{t("productVisibilityData.available")}</strong>{t("productVisibilityData.availabilityInfoAfter")}
        </p>
      </Modal>
    </>
  );
};
